import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FilterObject, FilterOptions, ListingsService } from '@out-web/core';
import { BehaviorSubject, debounceTime, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DetailComponent } from '../../atoms/detail/detail.component';

@Component({
  selector: 'out-listings-filter',
  standalone: true,
  imports: [CommonModule, DetailComponent, FormsModule, RouterLink],
  templateUrl: './listings-filter.component.html',
  styleUrl: './listings-filter.component.scss',
})
export class ListingsFilterComponent implements OnInit, OnDestroy {
  @Output() filterChange = new EventEmitter<FilterObject>();
  isFilterCardVisible = false;
  load: string | null = null;
  form: FormGroup;
  private isSettingValue = false;
  hasUpdatedFilters = false;
  private filterOptions$: BehaviorSubject<FilterOptions | null>;
  private destroy$ = new Subject<void>();
  searchTerms: { [key: string]: string } = {};

  options: FilterOptions = {
    model: [],
    year: [],
    listingType: [],
    condition: [],
    trim: [],
    autopilot: [],
    location: [],
  };

  selected = {
    dealer: 'ALL_OPTIONS',
    model: 'ALL_OPTIONS',
    year: 'ALL_OPTIONS',
    listingType: 'ALL_OPTIONS',
    condition: 'ALL_OPTIONS',
    trim: 'ALL_OPTIONS',
    autopilot: 'ALL_OPTIONS',
    location: 'ALL_OPTIONS',
  };

  constructor(
    private fb: FormBuilder,
    private listingsService: ListingsService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    this.filterOptions$ = new BehaviorSubject<FilterOptions | null>(null);

    this.form = this.fb.group({
      model: ['ALL_OPTIONS'],
      year: ['ALL_OPTIONS'],
      listingType: ['ALL_OPTIONS'],
      condition: ['ALL_OPTIONS'],
      trim: ['ALL_OPTIONS'],
      autopilot: ['ALL_OPTIONS'],
      location: ['ALL_OPTIONS'],
      dealer: ['ALL_OPTIONS'],
    });
  }

  ngOnInit() {
    this.initializeFormWithRouteParams();
    this.loadFilterOptions('skeleton');

    this.form.valueChanges
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => {
        this.onFilterChange();
      });

    this.activatedRoute.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        this.updateFormFromQueryParams(params);
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private onFilterChange() {
    const cleanedFilter = this.cleanFilter(this.form.value);

    const queryParams = { ...this.activatedRoute.snapshot.queryParams };

    Object.keys(this.form.value).forEach((key) => {
      if (this.form.value[key] !== 'ALL_OPTIONS') {
        queryParams[key] = this.form.value[key];
      } else {
        delete queryParams[key];
      }
    });

    if (this.hasFilterChanged(queryParams)) {
      this.router
        .navigate([], {
          queryParams,
        })
        .then(() => {
          this.listingsService
            .fetchFilters(cleanedFilter, true)
            .subscribe(
              (
                filterOptions: Record<
                  string,
                  { label: string; value: string }[]
                >
              ) => {
                const transformedFilterOptions: FilterOptions = {
                  model: filterOptions['model'] || [],
                  year: filterOptions['year'] || [],
                  listingType: filterOptions['listingType'] || [],
                  condition: filterOptions['condition'] || [],
                  trim: filterOptions['trim'] || [],
                  autopilot: filterOptions['autopilot'] || [],
                  location: filterOptions['location'] || [],
                };
                this.updateFilterOptions(cleanedFilter, filterOptions);
                this.filterOptions$.next(transformedFilterOptions);
                this.filterChange.emit(cleanedFilter);
              }
            );
        });
    }
  }

  private hasFilterChanged(newParams: { [key: string]: string }) {
    const currentParams = this.activatedRoute.snapshot.queryParams;
    return JSON.stringify(newParams) !== JSON.stringify(currentParams);
  }

  private initializeFormWithRouteParams() {
    const queryParams = this.activatedRoute.snapshot.queryParams;
    this.updateFormFromQueryParams(queryParams);
  }

  private updateFormFromQueryParams(params: { [key: string]: string }) {
    Object.keys(this.form.controls).forEach((controlName) => {
      const paramValue = params[controlName];
      if (paramValue && paramValue !== 'ALL_OPTIONS') {
        this.form.get(controlName)?.setValue(paramValue, { emitEvent: false });
        if (controlName === 'dealer') {
          this.selected.dealer = paramValue;
        }
      }
    });
  }

  loadFilterOptions(loadType: string): void {
    this.load = loadType;

    const cleanedFilter = this.cleanFilter(this.form.value);
    this.listingsService.fetchFilters(cleanedFilter, true).subscribe({
      next: (filterOptions) => {
        if (filterOptions) {
          this.updateFilterOptions(cleanedFilter, filterOptions);
          const transformedFilterOptions: FilterOptions = {
            model: filterOptions['model'] || [],
            year: filterOptions['year'] || [],
            listingType: filterOptions['listingType'] || [],
            condition: filterOptions['condition'] || [],
            trim: filterOptions['trim'] || [],
            autopilot: filterOptions['autopilot'] || [],
            location: filterOptions['location'] || [],
          };
          this.filterOptions$.next(transformedFilterOptions);
        }
        this.load = null;
      },
      error: (err) => {
        console.error(err);
        this.load = null;
      },
    });
  }

  private updateFilterOptions(
    selectedFilter: { [key: string]: string },
    filterOptions: Partial<FilterOptions>
  ) {
    Object.keys(this.options).forEach((key) => {
      const keyAsFilterOptionKey = key as keyof FilterOptions;
      const isAllSelected = selectedFilter[key] === 'ALL_OPTIONS';

      if (isAllSelected) {
        this.options[keyAsFilterOptionKey] = [
          { label: 'All', value: 'ALL_OPTIONS' },
          ...(filterOptions[keyAsFilterOptionKey] || []),
        ];
      } else {
        const selectedValue = selectedFilter[key];
        const selectedOption = (filterOptions[keyAsFilterOptionKey] ?? []).find(
          (option) => option.value === selectedValue
        );
        if (selectedOption) {
          this.options[keyAsFilterOptionKey] = [
            { label: 'All', value: 'ALL_OPTIONS' },
            ...(filterOptions[keyAsFilterOptionKey] || []),
          ];
        } else {
          this.options[keyAsFilterOptionKey] = [
            { label: 'All', value: 'ALL_OPTIONS' },
            ...(filterOptions[keyAsFilterOptionKey] || []),
          ];
        }
      }
    });
  }

  toggleFilterCard(): void {
    this.isFilterCardVisible = !this.isFilterCardVisible;
  }

  getControl(controlName: string): FormControl {
    return this.form.get(controlName) as FormControl;
  }

  onControlChange(controlName: string, newValue: string | number) {
    const control = this.getControl(controlName);
    const value = String(newValue);

    if (!this.isSettingValue && control.value !== value) {
      this.isSettingValue = true;
      control.setValue(value, { emitEvent: true });
      // if (value !== 'ALL_OPTIONS') {
      //   console.log('Not Emiited')
      //   control.setValue(value, { emitEvent: false });
      // } else {
      //   console.log('Emiited')
      //   control.setValue(value, { emitEvent: true });
      // }
      this.isSettingValue = false;
    }
  }

  private cleanFilter(filter: { [key: string]: string }) {
    const cleaned: { [key: string]: string } = {};
    Object.keys(filter).forEach((key) => {
      if (filter[key] !== 'ALL_OPTIONS') {
        cleaned[key] = filter[key];
      }
    });
    return cleaned;
  }

  clearFilters = () => {
    window.location.href =
      '/used-tesla-for-sale?OrderBy=created+DESC&Skip=9&Top=9';
  };

  toggleSelectFields(disable: boolean) {
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      if (control) {
        if (disable) {
          control.disable();
        } else {
          control.enable();
        }
      }
    });
  }

  filteredOptions(optionKey: string) {
    const searchTerm = this.searchTerms[optionKey] || '';
    return this.options[optionKey as keyof FilterOptions].filter(
      (item) =>
        item.label === 'All' ||
        item.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }
}
